/* Squigli seasonal snow: a fixed canvas that snows for as long as the page is open.
   Flakes are pre-drawn sprites (soft dots + a few crystals) with a faint shadow so they show on white too.
   Pauses while the tab is hidden; with "reduce motion" on it draws one still frame instead of animating. */
(() => {
  if (customElements.get('squigli-snow')) return;

  const sprite = (r, crystal) => {
    const pad = 4, s = Math.ceil((r + pad) * 2), c = document.createElement('canvas');
    c.width = c.height = s;
    const g = c.getContext('2d');
    g.translate(s / 2, s / 2);
    g.shadowColor = 'rgba(70, 100, 130, 0.35)';
    g.shadowBlur = 3;
    if (crystal) {
      g.strokeStyle = '#fff'; g.lineCap = 'round'; g.lineWidth = Math.max(1, r / 6);
      for (let i = 0; i < 6; i++) {
        g.save(); g.rotate((i * Math.PI) / 3);
        g.beginPath(); g.moveTo(0, 0); g.lineTo(0, -r);
        g.moveTo(0, -r * 0.55); g.lineTo(-r * 0.28, -r * 0.8); g.moveTo(0, -r * 0.55); g.lineTo(r * 0.28, -r * 0.8);
        g.stroke(); g.restore();
      }
    } else {
      const grad = g.createRadialGradient(0, 0, 0, 0, 0, r);
      grad.addColorStop(0, 'rgba(255,255,255,1)'); grad.addColorStop(0.65, 'rgba(255,255,255,0.95)'); grad.addColorStop(1, 'rgba(255,255,255,0)');
      g.fillStyle = grad; g.beginPath(); g.arc(0, 0, r, 0, Math.PI * 2); g.fill();
    }
    return c;
  };

  class SquigliSnow extends HTMLElement {
    connectedCallback() {
      this.canvas = document.createElement('canvas');
      this.canvas.className = 'squigli-snow';
      Object.assign(this.canvas.style, {position: 'fixed', inset: '0', width: '100%', height: '100%', pointerEvents: 'none', zIndex: '1'});
      this.append(this.canvas);
      this.ctx = this.canvas.getContext('2d');
      this.density = (Number(this.dataset.density) || 100) / 100;
      this.wind = Number(this.dataset.wind) || 0;
      this.crystals = this.dataset.crystals !== 'false';
      this.dots = [2, 3, 4.5, 6].map((r) => sprite(r, false));
      this.stars = [7, 10, 13].map((r) => sprite(r, true));
      this.still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.onResize = () => this.resize();
      window.addEventListener('resize', this.onResize);
      this.resize();
      this.flakes = Array.from({length: this.count()}, () => this.flake(true));
      if (this.still) { this.draw(0); return; }
      this.last = performance.now();
      this.tick = (now) => {
        const dt = Math.min(0.05, (now - this.last) / 1000);   // big gaps (tab was hidden) don't make flakes jump
        this.last = now;
        if (!document.documentElement.classList.contains('orn-typing')) this.draw(dt);   // rests while a name is typed (phones)
        this.raf = requestAnimationFrame(this.tick);
      };
      this.raf = requestAnimationFrame(this.tick);
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.raf);
      window.removeEventListener('resize', this.onResize);
    }

    count() {
      const area = window.innerWidth * window.innerHeight;
      return Math.round(Math.min(260, area / (window.innerWidth < 750 ? 7000 : 9000)) * this.density);
    }

    resize() {
      const dpr = Math.min(2, window.devicePixelRatio || 1), w = window.innerWidth, h = window.innerHeight;
      // phones change the height while scrolling (the address bar shows/hides): keep the tallest size for this width,
      // so the canvas isn't wiped and rebuilt on every scroll; only a new width (rotation) or a taller screen resizes it
      if (w === this.w && h <= this.h) return;
      if (w !== this.w) this.h = 0;
      this.w = w; this.h = Math.max(this.h || 0, h);
      this.canvas.style.height = `${this.h}px`;
      this.canvas.width = Math.round(this.w * dpr); this.canvas.height = Math.round(this.h * dpr);
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (this.flakes) {
        const want = this.count();
        while (this.flakes.length < want) this.flakes.push(this.flake(true));
        this.flakes.length = Math.min(this.flakes.length, want);
        if (this.still) this.draw(0);
      }
    }

    // depth 0 (far: small, slow, faint) .. 1 (near: big, fast)
    flake(anywhere) {
      const crystal = this.crystals && Math.random() < 0.09;
      const depth = Math.random();
      const img = crystal ? this.stars[Math.floor(Math.random() * this.stars.length)]
        : this.dots[Math.min(this.dots.length - 1, Math.floor(depth * this.dots.length))];
      return {
        img, crystal,
        x: Math.random() * this.w,
        y: anywhere ? Math.random() * this.h : -img.height,
        vy: 22 + depth * 55 + (crystal ? 10 : 0),
        sway: 8 + Math.random() * 22, phase: Math.random() * Math.PI * 2, freq: 0.4 + Math.random() * 0.9,
        spin: (Math.random() - 0.5) * 0.8, rot: Math.random() * Math.PI,
        alpha: 0.55 + depth * 0.45,
      };
    }

    draw(dt) {
      const {ctx, w, h} = this;
      ctx.clearRect(0, 0, w, h);
      for (let i = 0; i < this.flakes.length; i++) {
        let f = this.flakes[i];
        f.y += f.vy * dt;
        f.phase += f.freq * dt;
        f.x += (this.wind * 6 * (0.5 + f.alpha)) * dt;
        f.rot += f.spin * dt;
        if (f.y > h + f.img.height) { f = this.flakes[i] = this.flake(false); }
        if (f.x < -20) f.x += w + 40; else if (f.x > w + 20) f.x -= w + 40;
        const x = f.x + Math.sin(f.phase) * f.sway, s = f.img.width;
        ctx.globalAlpha = f.alpha;
        if (f.crystal) {
          ctx.save(); ctx.translate(x, f.y); ctx.rotate(f.rot); ctx.drawImage(f.img, -s / 2, -s / 2); ctx.restore();
        } else {
          ctx.drawImage(f.img, x - s / 2, f.y - s / 2);
        }
      }
      ctx.globalAlpha = 1;
    }
  }

  customElements.define('squigli-snow', SquigliSnow);
})();
